// Structured option vocabularies for Parish Intelligence™ profile dropdowns.
// Design principle: 90% structured fields, 10% narrative.

export const WORSHIP_MUSIC_OPTIONS = {
  primary_worship_tradition: ["Roman Catholic", "Episcopal / Anglican", "Lutheran", "Methodist", "Presbyterian", "Baptist", "Reformed", "Non-Denominational", "Orthodox", "Other"],
  worship_style: ["Traditional", "High Church", "Broad Church", "Low Church", "Anglo-Catholic", "Contemporary", "Blended", "Liturgical", "Charismatic", "Gospel", "Taizé", "Evensong", "Family Worship", "Student Ministry", "Seasonal Variations", "Other"],
  principal_instrument: ["Pipe Organ", "Digital Organ", "Grand Piano", "Upright Piano", "Keyboard", "Praise Band", "Organ & Piano", "Organ & Orchestra", "None", "Other"],
  congregational_singing_strength: ["Excellent", "Strong", "Moderate", "Weak", "Needs Development"],
  choir_program: ["Adult Choir", "Volunteer Choir", "Professional Choir", "Children's Choir", "Youth Choir", "Handbell Choir", "Men's Choir", "Women's Choir", "Schola Cantorum", "Contemporary Worship Team", "Seasonal Choir", "No Choir"],
  cantor_ministry: ["Always", "Frequently", "Occasionally", "Special Liturgies Only", "None"],
  hymnal_resources: {
    "Roman Catholic": ["Lumen Christi Hymnal", "Breaking Bread", "Today's Missal", "Heritage Missal", "Worship IV", "Gather", "Gather Comprehensive", "JourneySongs", "Spirit & Song", "Respond & Acclaim"],
    "Episcopal / Anglican": ["The Hymnal 1982", "Lift Every Voice and Sing II", "Wonder Love and Praise", "Voices Found", "Enriching Our Music"],
    "Lutheran": ["ELW", "LSB", "LBW", "With One Voice", "ACS"],
    "Methodist": ["United Methodist Hymnal", "The Faith We Sing", "Worship & Song", "Cokesbury resources"],
    "General": ["CCLI", "Planning Center Library", "Custom Parish Collection", "Other"],
  },
  psalm_method: ["Responsorial Psalm", "Anglican Chant", "Pointed Psalm", "Metrical Psalm", "Spoken", "Mixed"],
  gospel_acclamation: ["Always Sung", "Usually Sung", "Seasonally Sung", "Spoken"],
  mass_service_setting: ["Mass of Creation", "Mass of Renewal", "Mass of Christ the Savior", "Missa Simplex", "Service Music Varies", "Anglican Chant", "Custom Parish Setting", "Other"],
  choir_rehearsal_days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "No Regular Rehearsal"],
  active_choristers: ["0", "1–5", "6–10", "11–20", "21–35", "36–50", "50+"],
  typical_music_difficulty: ["Simple", "Moderate", "Advanced", "Professional", "Mixed"],
  music_planning_responsibility: ["Pastor", "Rector", "Director of Music", "Organist", "Choir Director", "Worship Committee", "Volunteer", "Shared Responsibility"],
  service_planning_frequency: ["Weekly", "Monthly", "Quarterly", "Seasonally", "Annually"],
  liturgical_planning_software: ["SundayBuilder™", "Planning Center", "Google Docs", "Microsoft Office", "Printed Binder", "Email", "Nothing Formal", "Other"],
  copyright_licensing: ["OneLicense", "CCLI", "CCS", "GIA License", "OCP License", "None", "Unknown"],
  // Stable id ↔ display-name pairs for the Primary Hymnal picker. The id
  // is what drives the backend Primary-Hymnal restriction; the display
  // name mirrors `hymnal_resources`. Actual visibility is filtered at
  // render time against `HYMNAL_ALLOWLIST_BY_TRADITION` — a Roman
  // Catholic parish only sees LCH; an Episcopal parish sees H1982 +
  // LEVAS2 + WLP; an Anglican (non-TEC) parish sees LEVAS2 + WLP only.
  primary_hymnal_source_id: [
    { value: "LCH",    label: "Lumen Christi Hymnal" },
    { value: "H1982",  label: "The Hymnal 1982" },
    { value: "LEVAS2", label: "Lift Every Voice and Sing II" },
    { value: "WLP",    label: "Wonder Love and Praise" },
  ],
  // Governed Organ Catalog · difficulty ceiling used by Ask-LI when
  // suggesting Prelude/Postlude repertoire. Values MUST match the
  // backend catalog enum in `repertoire/organ/catalog.py`
  // (DIFFICULTY_LEVELS). Stored as either null (uncapped) or one of
  // the four enum keys. Order below is ascending — the "No ceiling"
  // sentinel is rendered separately by WorshipMusicSection because
  // the empty-string option always maps to null on save.
  organ_difficulty_ceiling: [
    { value: "beginner", label: "Beginner" },
    { value: "moderate", label: "Moderate" },
    { value: "advanced", label: "Advanced" },
    { value: "virtuoso", label: "Virtuoso" },
  ],
  livestream_music: ["Always", "Frequently", "Occasionally", "Never"],
  special_music_traditions: ["Lessons & Carols", "Festival Eucharist", "Choral Evensong", "Nine Lessons & Carols", "Christmas Midnight Mass", "Holy Week Choir", "Brass Ensemble", "String Quartet", "Orchestra", "Gospel Choir", "Taizé Services", "Community Hymn Sings", "Organ Recital Series", "Silent Film Series", "Other"],
};

// Governance rule (owner directive 2026-02-14): "The 1982 hymnal is
// used only for Episcopal churches." Maps the parish's declared
// `primary_worship_tradition` to the set of `primary_hymnal_source_id`
// values it may pick. Keep in exact sync with
// `TRADITION_HYMNAL_ALLOWLIST` in `backend/repertoire/hymns/profile_sync.py`.
export const HYMNAL_ALLOWLIST_BY_TRADITION = {
  "Roman Catholic":       ["LCH"],
  "Episcopal / Anglican": ["H1982", "LEVAS2", "WLP"],  // combined bucket kept for legacy profiles
  "Episcopal":            ["H1982", "LEVAS2", "WLP"],
  "Anglican":             ["LEVAS2", "WLP"],           // explicitly NOT H1982
  "Lutheran":             [],
  "Methodist":            [],
  "Presbyterian":         [],
  "Reformed":             [],
  "Baptist":              [],
  "Non-Denominational":   [],
  "Orthodox":             [],
  "Other":                [],
};

// UI layout: grouped sections. type: select | multi | grouped_multi | time | area
export const WORSHIP_MUSIC_GROUPS = [
  {
    title: "Worship Identity",
    fields: [
      { key: "primary_worship_tradition", label: "Primary Worship Tradition", type: "select" },
      { key: "worship_style", label: "Worship Style", type: "multi", otherKey: "other_worship_style" },
      { key: "psalm_method", label: "Psalm Method", type: "select" },
      { key: "gospel_acclamation", label: "Gospel Acclamation", type: "select" },
      { key: "mass_service_setting", label: "Mass / Service Setting", type: "multi", otherKey: "other_mass_service_setting" },
      { key: "livestream_music", label: "Livestream Music", type: "select" },
    ],
  },
  {
    title: "Music Leadership & Ensembles",
    fields: [
      { key: "principal_instrument", label: "Principal Instrument", type: "select", otherKey: "other_principal_instrument" },
      { key: "congregational_singing_strength", label: "Congregational Singing Strength", type: "select" },
      { key: "choir_program", label: "Choir Program", type: "multi" },
      { key: "cantor_ministry", label: "Cantor Ministry", type: "select" },
      { key: "active_choristers", label: "Number of Active Choristers", type: "select" },
      { key: "typical_music_difficulty", label: "Typical Music Difficulty", type: "select" },
      { key: "choir_rehearsal_days", label: "Choir Rehearsal Days", type: "multi" },
      { key: "choir_rehearsal_time", label: "Choir Rehearsal Time", type: "time" },
    ],
  },
  {
    title: "Resources & Repertoire",
    fields: [
      { key: "primary_hymnal_source_id", label: "Primary Hymnal", type: "select", helper: "Congregational hymn recommendations will be restricted to this hymnal's verified crosswalk." },
      { key: "hymnal_resources", label: "Hymnal Resources", type: "grouped_multi", otherKey: "other_hymnal_resource" },
      { key: "organ_difficulty_ceiling", label: "Organ Repertoire Difficulty Ceiling", type: "select", noCeilingOption: true, helper: "Ask-LI will exclude organ works above this difficulty level." },
      { key: "copyright_licensing", label: "Copyright Licensing", type: "multi" },
      { key: "special_music_traditions", label: "Special Music Traditions", type: "multi", otherKey: "other_special_music_tradition" },
    ],
  },
  {
    title: "Planning & Operations",
    fields: [
      { key: "music_planning_responsibility", label: "Music Planning Responsibility", type: "multi" },
      { key: "service_planning_frequency", label: "Service Planning Frequency", type: "select" },
      { key: "liturgical_planning_software", label: "Liturgical Planning Software", type: "multi", otherKey: "other_liturgical_planning_software" },
    ],
  },
  {
    title: "Parish Musical Identity",
    fields: [
      {
        key: "musical_identity_description", label: "Describe your parish's musical identity.", type: "area",
        placeholder: "For example: “Our congregation strongly prefers Anglican hymnody, sings confidently in four-part harmony, celebrates Choral Evensong monthly, and places a high value on the organ tradition.”",
      },
    ],
  },
];

// High-value fields that drive the tab's completion fraction.
export const WORSHIP_MUSIC_HIGH_VALUE = [
  "primary_worship_tradition", "worship_style", "principal_instrument",
  "congregational_singing_strength", "choir_program", "hymnal_resources",
  "musical_identity_description",
];

// =============================================================================
// Phase 2 (P4) additions — structured selectors for former free-text fields
// =============================================================================

// US 50 + DC + territories + Canadian provinces + Other.
// Order: US states A→Z, then DC and territories, then Canadian provinces A→Z, then Other.
export const STATES_PROVINCES_TERRITORIES = [
  // United States
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia",
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
  "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
  "Washington", "West Virginia", "Wisconsin", "Wyoming",
  // US Territories
  "American Samoa", "Guam", "Northern Mariana Islands", "Puerto Rico",
  "U.S. Virgin Islands",
  // Canadian Provinces & Territories
  "Alberta", "British Columbia", "Manitoba", "New Brunswick",
  "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
  "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
  "Yukon",
  "Other",
];

// Eucharistic / Holy Communion prayers, keyed by denomination / tradition.
// The Field component reads the profile's tradition context and shows the
// matching list, falling back to General. Every list ends with "Not applicable"
// and "Other". Multi-select — parishes may regularly use several.
export const EUCHARISTIC_PRAYER_BY_DENOMINATION = {
  "Roman Catholic": [
    "Roman Canon (Eucharistic Prayer I)",
    "Eucharistic Prayer II",
    "Eucharistic Prayer III",
    "Eucharistic Prayer IV",
    "EP for Reconciliation I",
    "EP for Reconciliation II",
    "EP for Various Needs I",
    "EP for Various Needs II",
    "EP for Various Needs III",
    "EP for Various Needs IV",
    "EP for Masses with Children I",
    "EP for Masses with Children II",
    "EP for Masses with Children III",
    "Not applicable",
    "Other",
  ],
  "Episcopal / Anglican": [
    "Rite I · Prayer I",
    "Rite I · Prayer II",
    "Rite II · Prayer A",
    "Rite II · Prayer B",
    "Rite II · Prayer C",
    "Rite II · Prayer D",
    "Enriching Our Worship · Prayer 1",
    "Enriching Our Worship · Prayer 2",
    "Enriching Our Worship · Prayer 3",
    "An Order for Celebrating the Holy Eucharist",
    "Not applicable",
    "Other",
  ],
  "Lutheran": [
    "Setting One (Great Thanksgiving)",
    "Setting Two",
    "Setting Three",
    "Setting Four",
    "Setting Five",
    "Setting Six",
    "Setting Seven",
    "Setting Eight",
    "Setting Nine",
    "Setting Ten",
    "Not applicable",
    "Other",
  ],
  "Methodist": [
    "Word and Table I",
    "Word and Table II",
    "Word and Table III",
    "Word and Table IV",
    "Word and Table V",
    "A Service of Word and Table for General Use",
    "Not applicable",
    "Other",
  ],
  "Presbyterian": [
    "Book of Common Worship · Great Thanksgiving A",
    "Book of Common Worship · Great Thanksgiving B",
    "Book of Common Worship · Great Thanksgiving C",
    "Not applicable",
    "Other",
  ],
  // Fallback for denominations without a codified list. The user can still
  // choose "Other" and enter their own communion prayer form.
  "General": [
    "Standard Communion Prayer",
    "Extended Consecration",
    "Communion Meditation",
    "Not applicable",
    "Other",
  ],
};

// Service-music tag-input seed. These are suggestions only — parishes may
// add uncatalogued entries and NOTHING is silently altered on save.
export const SERVICE_MUSIC_SUGGESTIONS = [
  "Mass of Creation",
  "Mass of Renewal",
  "Mass of Christ the Savior",
  "Mass of Wisdom",
  "Missa Simplex",
  "Missa Emmanuel",
  "Community Mass",
  "Storrington Mass",
  "Belmont Mass",
  "Anglican Chant",
  "Hymnal 1982 Service Music (S 129 · S 130 · S 236)",
  "David Hurd · New Plainsong",
  "Franz Schubert · Deutsche Messe",
  "Merbecke",
  "Setting One (ELW)",
  "Setting Three (ELW)",
  "Setting Eight (ELW)",
  "Word and Table Setting I",
  "Word and Table Setting II",
  "Word and Table Setting III",
];

// Resolve which denomination-keyed list to use for the Eucharistic Prayer
// multi. Reads all the fallback candidate paths that `_profile_bits` uses.
export function resolveTraditionKey(profile) {
  const wm = profile?.worship_music || {};
  const wi = profile?.worship_identity || {};
  const id = profile?.identity || {};
  const candidates = [
    wm.primary_worship_tradition,
    wi.liturgical_tradition,
    id.worship_tradition,
    id.denomination,
  ];
  const known = Object.keys(EUCHARISTIC_PRAYER_BY_DENOMINATION).filter((k) => k !== "General");
  for (const c of candidates) {
    if (!c) continue;
    const raw = String(c).trim();
    if (known.includes(raw)) return raw;
    // slash-alias tolerance: "Episcopal / Anglican" → try either segment
    for (const seg of raw.split("/").map((s) => s.trim())) {
      if (known.includes(seg)) return seg;
    }
  }
  return "General";
}
